/**
 * The draft a live collab session is opened for (PSTUDIO-4449): the solution
 * and its draft version, by the database uuids collab-service names rooms by.
 *
 * The two ids reach the context at different times. Opening another solution
 * assigns `selectedBot` straight away, while `solutionVersionPsId` only follows
 * when `loadBotVersions` resolves — so for a few round trips the context pairs
 * the new solution with the PREVIOUS solution's draft. `loadBotVersions` records
 * which solution its draft id belongs to (`solutionVersionPsSolutionId`), and
 * the pair is handed out only when that owner is the selected solution.
 *
 * The solution uuid is `selectedBot.uniqueId` — the id the editor route is built
 * from. Not `selectedBot.id`, which a solution loaded on main does not have, and
 * not the `${customerId}.${botName}` key.
 */
export type CollabDraftIdentity = {
  solutionId: string;
  solutionVersionId: string;
};

export const collabDraftIdentity = ({
  selectedBot,
  routeSolutionId,
  solutionVersionPsId,
  solutionVersionPsSolutionId,
}: {
  selectedBot?: { uniqueId?: string };
  /** The solution uuid in the page's URL (`:botId`), not from the context. */
  routeSolutionId?: string | null;
  solutionVersionPsId?: string | null;
  solutionVersionPsSolutionId?: string | null;
}): CollabDraftIdentity | undefined => {
  if (!solutionVersionPsId || !solutionVersionPsSolutionId) return undefined;
  // PSTUDIO-4452: publish and the post-publish reload clear `selectedBot` and
  // load it again while the draft and its owner stay — keep the pair then,
  // or the live session drops and reconnects around every publish. Leaving
  // the solution clears the owner too (`clearedSolutionScope`) — but deleting
  // one only clears `selectedBot`, so the pair is kept only while the URL
  // still names its owner. The route, not `ctx.botIdURLParam`: the machine
  // clears that one as soon as it has resolved the solution's name.
  if (!selectedBot) {
    if (routeSolutionId !== solutionVersionPsSolutionId) return undefined;
    return {
      solutionId: solutionVersionPsSolutionId,
      solutionVersionId: solutionVersionPsId,
    };
  }
  const solutionId = selectedBot.uniqueId;
  if (!solutionId || solutionVersionPsSolutionId !== solutionId) {
    return undefined;
  }
  return { solutionId, solutionVersionId: solutionVersionPsId };
};
